import {
  Dispatch,
  SetStateAction,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Alert, Button } from "reactstrap";
import { Modal } from "rsuite";

import useFilePreview from "../../../../api/api-hooks/records/useFilePreview";
import { FileWithDefaults } from "../../../../types/FileInterfaces";
import AppCopy from "../../../../components/AppComponents/AppCopy";
import { downloadFile } from "../../../../utils/FileUtils";
import IconButton from "../../../../elements/IconButton";
import AppSpinner from "../../../../elements/AppSpinner";
import useKeyEvent from "../../../../hooks/useKeyEvent";
import Constants from "../../../../utils/Constants";
import Preview from "../../../../utils/Preview";
import ImageInfoDrawer from "./ImageInfoDrawer";
import { FileRecord } from "../../Library";

interface Props {
  isOpen: boolean;
  files: FileWithDefaults[];
  selected: number;
  submitting?: boolean;
  record?: any;
  closeModal: () => void;
  deleteFile: ((fileID: string, afterDeletion: () => void) => void) | undefined;
  nextFile: (() => void) | undefined;
  previousFile: (() => void) | undefined;
  setSelected: ((data: any) => void) | undefined;
  onExited?: () => void;
  setOpenInfo: Dispatch<SetStateAction<FileRecord | null>>;
  openInfo: FileRecord | null;
  handleUpdateFile: (
    newValue: string,
    key: "Name" | "Caption" | "Transcript",
    onSuccess?: () => void
  ) => void;
}

const GalleryPreviewModal = ({
  files,
  selected,
  isOpen,
  submitting,
  record,
  closeModal,
  deleteFile,
  nextFile,
  previousFile,
  handleUpdateFile,
  setSelected,
  onExited,
  setOpenInfo,
  openInfo,
}: Props) => {
  if (files?.length === 0 || selected === undefined) return null;

  const selectedRecord = files?.[selected];
  const file = selectedRecord?.File;
  const transcript = selectedRecord?.File?.Transcript;

  const { fileSource, loading, previewError, fileType, extension } =
    useFilePreview(file);
  const [caption, setCaption] = useState("");
  const [transcriptState, setTranscriptState] = useState("");
  const [editingTranscript, setEditingTranscript] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);
  useKeyEvent(
    previousFile,
    Constants.KEYS.LEFT,
    selected === 0 || !previousFile || editingTranscript
  );
  useKeyEvent(
    nextFile,
    Constants.KEYS.RIGHT,
    selected === files.length - 1 || !nextFile || editingTranscript
  );

  useEffect(() => {
    setTranscriptState(transcript || "");
  }, [transcript]);

  useEffect(() => {
    setCaption(file?.Caption || "");
  }, [file?.Caption]);

  const previewVal = useMemo(() => {
    return { ...selectedRecord, source: fileSource };
  }, [selectedRecord, fileSource]);

  return (
    <Modal
      size="lg"
      overflow={false}
      open={isOpen}
      onClose={() => {
        setEditingTranscript(false);
        setTranscriptState(transcript || "");
        closeModal();
      }}
      onExited={onExited}
      style={{ height: "100vh", overflow: "hidden" }}
    >
      <div
        style={{
          height: "100%",
          minHeight: 400,
          backgroundColor: "#1E1E1E",
          flex: 1,
        }}
        className="rounded tw:flex flex-column"
      >
        <header className="tw:flex justify-content-between pt-2 px-3">
          <div
            className="text-white font-weight-bold tw:flex align-items-center"
            style={{ width: "calc(100% - 100px)" }}
          >
            {file?.Type?.startsWith("image") && (
              <img
                ref={imgRef}
                style={{ width: "30px", maxHeight: "30px" }}
                src={fileSource}
                alt=""
                className="mr-3"
              />
            )}
            <div className="text-truncate w-100">{file?.Name}</div>
          </div>
          <div className="text-white tw:flex" style={{ fontSize: "26px" }}>
            <a href={fileSource} rel="noreferrer" target="blank">
              <i className={`mdi mdi-open-in-new text-white`} />
            </a>
            <i
              className={`mdi mdi-close cursor-pointer ml-3`}
              onClick={closeModal}
            />
          </div>
        </header>
        <div className="tw:flex" style={{ flex: 1 }}>
          <div className="tw:flex flex-column justify-content-center px-4">
            {previousFile ? (
              <IconButton
                onClick={previousFile}
                icon="mdi mdi-chevron-left"
                disabled={selected === 0}
                size="xl"
                className="gallery-button tw:flex justify-content-center align-items-center "
              />
            ) : (
              <div className="tw:md:w-10"></div>
            )}
          </div>
          <div className="w-100 h-100 tw:flex justify-content-center align-items-center py-2">
            {loading ? (
              <AppSpinner style={{ height: 80, width: 80, fontSize: 32 }} />
            ) : fileType === "Image" ? (
              <img
                ref={imgRef}
                style={{
                  maxWidth: "calc(100vw - 180px)",
                  maxHeight: "calc(100vh - 325px)",
                  objectFit: "contain",
                }}
                src={fileSource}
                alt=""
              />
            ) : ["Text", "PDF"].includes(fileType) ? (
              <div
                className="tw:flex justify-content-center align-items-center"
                style={{
                  height: "100%",
                  width: "100%",
                  maxHeight: "calc(100vh - 325px)",
                }}
              >
                {!previewError ? (
                  <Preview
                    type={fileType}
                    mode="record"
                    value={previewVal}
                    extension={extension}
                    styles={{ background: "white" }}
                  />
                ) : (
                  <Alert color="light">
                    No preview available for this file.
                  </Alert>
                )}
              </div>
            ) : fileType === "Video" ? (
              <div className="tw:flex tw:flex-col tw:justify-center tw:items-center tw:w-full">
                {transcript && (
                  <>
                    <style>{`
                      /* width */
                      ::-webkit-scrollbar {
                        width: 8px;
                        background: transparent; 
                      }
                      /* thumb */
                      ::-webkit-scrollbar-thumb {
                        background-color: rgba(255, 255, 255, 0.3); 
                        border-radius: 4px;
                      }
                    `}</style>
                    <div className="tw:flex tw:justify-between tw:items-center tw:w-full tw:pl-2 tw:mb-4">
                      <h3 className="tw:text-white tw:mb-0">Transcript</h3>
                      {editingTranscript ? (
                        <div className="tw:flex">
                          <Button
                            size="sm"
                            onClick={() => {
                              setEditingTranscript(false);
                              setTranscriptState(transcript);
                            }}
                            disabled={submitting}
                          >
                            Cancel
                          </Button>
                          <Button
                            size="sm"
                            color="primary"
                            className="tw:px-5"
                            onClick={() => {
                              saveTranscript();
                              setEditingTranscript(false);
                            }}
                            disabled={submitting}
                          >
                            {submitting ? <AppSpinner color="white" /> : "Save"}
                          </Button>
                        </div>
                      ) : (
                        <div className="tw:flex">
                          <AppCopy
                            text={transcript}
                            color="white"
                            hideTooltip
                          />
                          <i
                            className="mdi mdi-pencil tw:text-2xl tw:text-white tw:cursor-pointer tw:hover:text-dark tw:hover:bg-superlight tw:h-8 tw:w-8 tw:flex tw:justify-center tw:items-center tw:rounded"
                            onClick={() => setEditingTranscript(true)}
                          />
                        </div>
                      )}
                    </div>
                    {editingTranscript ? (
                      <textarea
                        className="tw:w-full tw:text-white tw:px-2 tw:h-screen"
                        style={{ maxHeight: "calc(100vh - 450px)" }}
                        value={transcriptState}
                        onChange={(e) => setTranscriptState(e.target.value)}
                        autoFocus
                      />
                    ) : (
                      <div
                        className="tw:w-full tw:text-white tw:overflow-y-scroll tw:px-2 tw:h-screen"
                        style={{ maxHeight: "calc(100vh - 450px)" }}
                      >
                        {transcript}
                      </div>
                    )}
                  </>
                )}
                <video
                  className="tw:w-full"
                  style={{
                    maxHeight: transcript ? 60 : "calc(100vh - 350px)",
                  }}
                  src={fileSource}
                  controls
                ></video>
              </div>
            ) : fileType === "Audio" ? (
              <audio
                style={{
                  width: "100%",
                  height: "50px",
                  minWidth: "300px",
                  paddingRight: "30px",
                }}
                controls
              >
                <source src={fileSource}></source>
              </audio>
            ) : (
              <div style={{ width: "100%", maxHeight: "500px" }}>
                <h2 className="text-center">
                  Preview not available for this file type.
                </h2>
              </div>
            )}
          </div>
          <div className="tw:flex flex-column justify-content-center px-4">
            {nextFile ? (
              <IconButton
                onClick={nextFile}
                icon="mdi mdi-chevron-right"
                disabled={selected === files.length - 1}
                size="xl"
                className="gallery-button tw:flex justify-content-center align-items-center "
              />
            ) : (
              <div className="tw:md:w-10"></div>
            )}
          </div>
        </div>

        <div className={`tw:flex justify-content-center`}>
          <div style={{ width: "100%", maxWidth: 600 }}>
            <div className="tw:flex justify-content-between px-2">
              <h4 className="m-0 text-white">Caption:</h4>
              <div>{caption.length} / 80</div>
            </div>
            <div className="tw:flex justify-content-between align-items-center">
              <textarea
                style={{ resize: "none", overflow: "hidden" }}
                rows={2}
                placeholder="Write your caption"
                value={caption}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    if (file?.Caption === caption) return;

                    handleUpdateFile(caption, "Caption");
                  }
                }}
                onChange={(e) => {
                  if (e.target.value.length > 80) return;
                  setCaption(e.target.value);
                }}
                className="bg-transparent text-white border-0 w-100 pl-2"
                disabled={submitting}
              />
              {submitting ? (
                <div className="px-2" style={{ height: 32 }}>
                  <AppSpinner />
                </div>
              ) : (
                <IconButton
                  onClick={() => handleUpdateFile(caption, "Caption")}
                  icon="mdi mdi-check"
                  color="primary"
                  disabled={file?.Caption === caption}
                  className="mr-2"
                />
              )}
            </div>
          </div>
        </div>

        <div className={`tw:flex justify-content-center`}>
          <div
            style={{
              boxShadow: "rgba(0, 0, 0, 0.15) 0px 2px 8px",
              borderRadius: "4px",
              backgroundColor: "#424342",
            }}
            className="mb-4 tw:flex align-items-center"
          >
            <GalleryOptionButton
              title="Download"
              icon="download"
              onClick={() => downloadFile({ ...file, URL: fileSource })}
            />

            <GalleryOptionButton
              title="Info"
              icon="information-slab-circle-outline"
              onClick={() =>
                setOpenInfo({
                  file,
                  recordID: selectedRecord?.id ?? "",
                })
              }
            />
            {deleteFile && setSelected && (
              <GalleryOptionButton
                title="Delete"
                icon="delete"
                onClick={() => {
                  deleteFile(file?.id, () => {
                    setSelected((prev: number) => {
                      const totalFiles = files.length;
                      if (totalFiles === 1 || prev === 0) return prev;
                      return prev - 1;
                    });
                  });
                }}
              />
            )}
          </div>
        </div>
      </div>
      {openInfo && (
        <ImageInfoDrawer
          file={file}
          open={!!openInfo && typeof selected === "number"}
          parentRecordID={record?.id}
          onClose={() => setOpenInfo(null)}
          record={record}
          submitting={submitting || false}
          onNameUpdate={(payload: any) =>
            handleUpdateFile(payload?.Name, "Name")
          }
        />
      )}
    </Modal>
  );

  function saveTranscript() {
    if (transcriptState === transcript) return setEditingTranscript(false);

    handleUpdateFile(transcriptState, "Transcript", () => {
      setEditingTranscript(false);
    });
  }
};

function GalleryOptionButton({
  onClick,
  icon,
  title,
}: {
  onClick: () => void;
  icon: string;
  title: string;
}) {
  const ref = useRef<any>();

  return (
    <button
      ref={ref}
      onClick={onClick}
      onMouseUp={() => ref?.current?.blur()}
      style={{ width: "100px" }}
      className="btn btn-outline-secondary gallery-button"
    >
      <i style={{ fontSize: "18px" }} className={`mdi mdi-${icon} m-0`} />
      <p className="mb-0" style={{ fontSize: "14px", fontWeight: "500" }}>
        {title}
      </p>
    </button>
  );
}

export default GalleryPreviewModal;
